import { selectApiData, selectApiNoContent } from 'util/selector/apiSelector';
import { selectResponseData } from 'util/selector/axiosSelector';

import { ApiMessage } from 'api/const';
import { WithData } from 'api/interfaces';
import { AxiosInstance } from 'axios';
import { PaginationModel } from 'model/Pagination.dto';
import { ProjectKind } from 'model/ProjectKind';
import qs from 'qs';

import {
  mapProjectTemplateDtoToModel,
  mapTemplateImportPreviewDtoToModel,
  ProjectTemplateDto,
  ProjectTemplateModel,
  ProjectTemplatePostDto,
  TemplateImportPreviewDto,
  TemplateImportPreviewModel,
} from './dto/ProjectTemplate.dto';

export namespace ProjectTemplateApi {}

export class ProjectTemplateApi {
  constructor(private client: AxiosInstance) {}

  list(): Promise<ProjectTemplateModel[]> {
    return this.client
      .get('/api/v1/project-template')
      .then((response) => selectResponseData(response, '_embedded.items', []))
      .then((list = []) => list.map(mapProjectTemplateDtoToModel));
  }

  listPaginated({
    page,
    limit,
    circleId,
    kind,
    sort,
  }: {
    page: number;
    limit: number;
    circleId?: string;
    kind?: ProjectKind;
    sort?: string;
  }): Promise<{ list: ProjectTemplateModel[]; pagination: PaginationModel }> {
    const query = qs.stringify(
      { page, limit, sort, filter: { circleId, kind } },
      { encode: false, skipNulls: true, addQueryPrefix: true },
    );

    return this.client
      .get(`/api/v1/project-template${query}`)
      .then((response) => selectResponseData(response) || [])
      .then((entry) => ({
        list: (entry?._embedded?.items ?? []).map(mapProjectTemplateDtoToModel),
        pagination: entry,
      }));
  }

  delete(id: number): Promise<void> {
    return this.client
      .delete<ApiMessage.Deleted>(`/api/v1/project-template/${id}`)
      .then(selectApiNoContent);
  }

  get(id: number): Promise<ProjectTemplateModel> {
    return this.client
      .get<WithData<ProjectTemplateDto>>(`/api/v1/project-template/${id}`)
      .then(selectApiData)
      .then(mapProjectTemplateDtoToModel);
  }

  create(data: ProjectTemplatePostDto): Promise<ProjectTemplateModel> {
    return this.client
      .post<WithData<ProjectTemplateDto>>(`/api/v1/project-template`, data)
      .then(selectApiData)
      .then(mapProjectTemplateDtoToModel);
  }

  importPreview(file: File): Promise<TemplateImportPreviewModel> {
    const data = new FormData();
    data.append('file', file);

    return this.client
      .post<WithData<TemplateImportPreviewDto>>(`/api/v1/project-template/import/preview`, data)
      .then(selectApiData)
      .then(mapTemplateImportPreviewDtoToModel);
  }

  import(circleId: string, file: File): Promise<ProjectTemplateModel> {
    const data = new FormData();
    data.append('file', file);

    return this.client
      .post<WithData<ProjectTemplateDto>>(`/api/v1/project-template/import/${circleId}`, data)
      .then(selectApiData)
      .then(mapProjectTemplateDtoToModel);
  }

  adapt(sourceId: number, destinationId: number): Promise<ProjectTemplateModel> {
    return this.client
      .post<
        WithData<ProjectTemplateDto>
      >(`/api/v1/project-template/${sourceId}/copy-to-project/${destinationId}`)
      .then(selectApiData)
      .then(mapProjectTemplateDtoToModel);
  }

  adaptPrevious(sourceId: number, destinationId: number): Promise<ProjectTemplateModel> {
    return this.client
      .post(`/api/v1/project/${sourceId}/copy-to-project/${destinationId}`)
      .then(selectApiData)
      .then(mapProjectTemplateDtoToModel);
  }
}
